import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calculator, TrendingUp, DollarSign, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EMICalculator() {
  const [searchParams] = useSearchParams();
  const [loanAmount, setLoanAmount] = useState(searchParams.get('amount') || '5000000');
  const [interestRate, setInterestRate] = useState('8.5');
  const [tenureYears, setTenureYears] = useState('20');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateEMI = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculator/emi`, {
        loan_amount: parseFloat(loanAmount),
        interest_rate: parseFloat(interestRate),
        tenure_months: parseInt(tenureYears) * 12
      });
      setResult(response.data);
    } catch (error) {
      console.error('Error calculating EMI:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loanAmount && interestRate && tenureYears) {
      calculateEMI();
    }
    // eslint-disable-next-line
  }, []);

  const handleCalculate = (e) => {
    e.preventDefault();
    calculateEMI();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calculator className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="calculator-heading">
              EMI Calculator
            </h1>
            <p className="text-lg text-slate-600">
              Calculate your home loan EMI and plan your finances
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100" data-testid="calculator-form">
              <h2 className="text-2xl font-heading font-semibold mb-6">Loan Details</h2>
              
              <form onSubmit={handleCalculate} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Loan Amount (₹) *
                  </label>
                  <Input
                    data-testid="loan-amount"
                    type="number"
                    required
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    placeholder="e.g., 5000000"
                  />
                  <div className="mt-2">
                    <input
                      type="range"
                      min="100000"
                      max="50000000"
                      step="100000"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>₹1L</span>
                      <span>₹5Cr</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Interest Rate (% per annum) *
                  </label>
                  <Input
                    data-testid="interest-rate"
                    type="number"
                    step="0.1"
                    required
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    placeholder="e.g., 8.5"
                  />
                  <div className="mt-2">
                    <input
                      type="range"
                      min="5"
                      max="20"
                      step="0.1"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>5%</span>
                      <span>20%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Loan Tenure (Years) *
                  </label>
                  <Input
                    data-testid="tenure-years"
                    type="number"
                    required
                    value={tenureYears}
                    onChange={(e) => setTenureYears(e.target.value)}
                    placeholder="e.g., 20"
                  />
                  <div className="mt-2">
                    <input
                      type="range"
                      min="1"
                      max="30"
                      step="1"
                      value={tenureYears}
                      onChange={(e) => setTenureYears(e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>1 Year</span>
                      <span>30 Years</span>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="calculate-button"
                  type="submit"
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-medium"
                  disabled={loading}
                >
                  {loading ? 'Calculating...' : 'Calculate EMI'}
                </Button>
              </form>
            </div>

            {/* Results */}
            <div className="space-y-6">
              {result && (
                <>
                  <div className="bg-primary p-8 rounded-lg text-white" data-testid="emi-result">
                    <p className="text-sm mb-2 opacity-90">Monthly EMI</p>
                    <p className="text-5xl font-bold">
                      ₹{result.monthly_emi?.toLocaleString('en-IN')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white p-6 rounded-lg border border-slate-100" data-testid="total-amount">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-sm text-slate-600">Total Amount</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-800">
                        ₹{result.total_amount?.toLocaleString('en-IN')}
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-lg border border-slate-100" data-testid="total-interest">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-secondary" />
                        </div>
                        <span className="text-sm text-slate-600">Total Interest</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-800">
                        ₹{result.total_interest?.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="bg-white p-6 rounded-lg border border-slate-100" data-testid="emi-breakdown">
                    <h3 className="text-lg font-semibold mb-4">Payment Breakdown</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Principal Amount</span>
                        <span className="font-semibold">₹{parseFloat(loanAmount).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Total Interest</span>
                        <span className="font-semibold text-secondary">₹{result.total_interest?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="border-t pt-3 flex justify-between items-center">
                        <span className="text-slate-800 font-semibold">Total Payment</span>
                        <span className="font-bold text-lg text-primary">₹{result.total_amount?.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Visual Representation */}
                  <div className="bg-white p-6 rounded-lg border border-slate-100">
                    <h3 className="text-lg font-semibold mb-4">Loan Split</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-slate-600">Principal</span>
                          <span className="font-medium">
                            {((parseFloat(loanAmount) / result.total_amount) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: `${(parseFloat(loanAmount) / result.total_amount) * 100}%`
                            }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-slate-600">Interest</span>
                          <span className="font-medium">
                            {((result.total_interest / result.total_amount) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-secondary"
                            style={{
                              width: `${(result.total_interest / result.total_amount) * 100}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!result && (
                <div className="bg-white p-12 rounded-lg border border-slate-100 text-center">
                  <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Enter loan details to calculate EMI</p>
                </div>
              )}
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-12 bg-slate-50 p-8 rounded-lg border border-slate-100">
            <h2 className="text-2xl font-heading font-semibold mb-4">Understanding EMI</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold mb-2">What is EMI?</h3>
                <p className="text-sm text-slate-600">
                  EMI (Equated Monthly Installment) is the fixed payment amount made by a borrower to a lender at a specified date each month.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">How it's Calculated</h3>
                <p className="text-sm text-slate-600">
                  EMI = [P x R x (1+R)^N]/[(1+R)^N-1], where P is principal, R is monthly interest rate, and N is tenure in months.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Factors Affecting EMI</h3>
                <p className="text-sm text-slate-600">
                  Loan amount, interest rate, and tenure are the three key factors that determine your monthly EMI payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
